import { useMemo } from "react";
import { useSelector } from "react-redux";

import { capitalizeEachWord } from "@/helpers/string";
import { RootState } from "@/store";
import { AggregationType, OrderByType, WidgetType } from "@/types/widget";
import { removeSpecialCharacters } from "@/utils/string";

import { DIMENSIONAL_VALUES_SHOWN } from "./constant";

interface UseWidgetConfigurationProps {
  datasetId: string;
  widgetType: WidgetType;
  measure: string;
  /** When present, dimension and measure options are sourced from rawDatasets columns
   *  instead of the schema — the analysis result may contain enriched attributes
   *  not present in the pre-analysis schema. */
  analysisId?: string;
}

const toOption = (value: string, label: string) => ({ value, label });

const ALL_AGGREGATION_OPTIONS = Object.values(AggregationType).map((agg) =>
  toOption(agg, removeSpecialCharacters(agg))
);

const orderByOptions = Object.values(OrderByType).map((orderBy) =>
  toOption(orderBy, orderBy)
);

const dimensionValueShownOptions = DIMENSIONAL_VALUES_SHOWN.map((num) =>
  toOption(num, num)
);

const useWidgetConfiguration = ({
  datasetId,
  widgetType,
  measure,
  analysisId
}: UseWidgetConfigurationProps) => {
  const { datasets, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );

  const datasetOptions = useMemo(
    () =>
      Object.values(datasets)
        .reverse()
        .map((dataset) => {
          if (dataset.data.published) {
            return toOption(
              dataset.data.id,
              dataset.data.display_name || dataset.data.name
            );
          }
          return false;
        })
        .filter(
          (opt): opt is { value: string; label: string } => opt !== false
        ),
    [datasets]
  );

  // H3 datasets and post-analysis context both use rawDatasets columns for options.
  const dimensionOptions = useMemo(() => {
    const columns = rawDatasets?.[datasetId]?.columns ?? [];
    if (
      analysisId ||
      datasets[datasetId]?.data.spatial_aggregation_type === "H3"
    ) {
      return columns
        .filter((s) => s.is_dimension)
        .map((s) => toOption(s.code || "", s.name));
    }
    return (datasets[datasetId]?.data.schema ?? [])
      .filter((s) => s.is_dimension)
      .map((s) => toOption(s.attribute_column || "", s.attribute_name));
  }, [analysisId, datasets, datasetId, rawDatasets]);

  // LINE widgets do not support COUNT_DISTINCT aggregation.
  // when measure have no value, only show COUNT and COUNT_DISTINCT
  const aggregationOptions = useMemo(() => {
    if (widgetType === WidgetType.LINE) {
      return ALL_AGGREGATION_OPTIONS.filter(
        (option) => option.value !== AggregationType.COUNT_DISTINCT
      );
    } else if (!measure && widgetType !== WidgetType.BIG_NUMBER) {
      return ALL_AGGREGATION_OPTIONS.filter((option) =>
        option.value.includes("COUNT")
      );
    } else {
      return ALL_AGGREGATION_OPTIONS;
    }
  }, [widgetType, measure]);

  const measureOptions = useMemo(() => {
    const columns = rawDatasets?.[datasetId]?.columns ?? [];

    // H3 datasets always use rawDataset columns directly; no breakdown grouping applies.
    if (datasets[datasetId]?.data.spatial_aggregation_type === "H3") {
      return columns
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.code || "", s.name));
    }

    let measureItems: { value: string; label: string }[];
    let breakdownGroupIds: string[];

    if (analysisId) {
      // Post-analysis: source from rawDatasets columns, code → value, name → label
      measureItems = columns
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.code || "", s.name));
      breakdownGroupIds = [
        ...new Set(
          columns
            .filter((s) => s.breakdown_group_id)
            .map((s) => s.breakdown_group_id!)
        )
      ];
    } else {
      const schema = datasets[datasetId]?.data.schema;
      if (!schema) return [];

      measureItems = schema
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.attribute_column || "", s.attribute_name));
      breakdownGroupIds = [
        ...new Set(
          schema
            .filter((s) => s.breakdown_group_id)
            .map((s) => s.breakdown_group_id!)
        )
      ];
    }

    if (widgetType === WidgetType.BIG_NUMBER || widgetType === WidgetType.DONUT)
      return measureItems;

    // WIDE_DATA datasets group multiple columns under a shared breakdown_group_id.
    // We surface one option per group rather than listing each column separately.
    if (!breakdownGroupIds.length) return measureItems;

    return [
      ...breakdownGroupIds.map((id) =>
        toOption(id, removeSpecialCharacters(capitalizeEachWord(id)))
      ),
      ...measureItems
    ];
  }, [analysisId, datasetId, datasets, rawDatasets, widgetType]);

  return {
    datasetOptions,
    dimensionOptions,
    measureOptions,
    aggregationOptions,
    orderByOptions,
    dimensionValueShownOptions
  };
};

export default useWidgetConfiguration;
